import React, { useState, useEffect, useRef } from "react";
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { format } from "date-fns";
import { enUS } from 'date-fns/locale';
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import axiosInstance from 'src/common/axiosInstance';
import exportExcelIcon from "../../assets/images/exportexcel.png";
import exportCSVIcon from "../../assets/images/exportcsv.png";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../slices/store";
import { setSelectedLineItems as setReduxSelectedLineItems } from "../../slices/Filters/reducer";
import LineItemDropdown from '../../components/Common/LineItemDropdown';
import "./emailReport.css"
import useIsMobile from "src/hooks/useIsMobile";

interface EmailReportProps {
  showModal?: boolean;
  onClose?: () => void;
  isEditMode?: boolean;
  editData?: any;
  selectedOrder: string;
  selectedItems: any[];
  currentStartDate: string;
  currentEndDate: string;
  dateRangeLabel?: string;
  selectedAgency: string;
  reportDataBy?: string;
}

interface ScheduleConfig {
  time?: string;
  week?: "OneWeek" | "TwoWeek" | "ThreeWeek";
  days?: string;
  month?: string;
}

const EmailReport: React.FC<EmailReportProps> = ({
  showModal,
  onClose,
  isEditMode,
  editData,
  selectedOrder,
  selectedItems,
  currentStartDate,
  currentEndDate,
  dateRangeLabel,
  selectedAgency,
  reportDataBy
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const [modalLarge, setModalLarge] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [attachmentType, setAttachmentType] = useState("CSV");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [scheduleType, setScheduleType] = useState("Daily");
  const [repeatEvery, setRepeatEvery] = useState("OneWeek");
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [selectedLineItems, setSelectedLineItems] = useState<any[]>([]);
  const [includeAiSummary, setIncludeAiSummary] = useState(false);

    const isMobile = useIsMobile();

  // Fetch line items when order changes
  useEffect(() => {
    const fetchLineItems = async () => {
      if (!selectedOrder) return;
      try {
        const response = await axiosInstance.get(`/line-items`, {
          params: { order_id: selectedOrder }
        });
        if (response.data.length) {
          setLineItems(response.data);
          // In add mode, always default to all line items
          if (!isEditMode) {
            const allItemIds = response.data.map((item: any) => item.Id);
            setSelectedLineItems(allItemIds);
            // Don't sync with Redux to avoid side effects
          }
        } else {
          setLineItems([]);
          setSelectedLineItems([]);
        }
      } catch (error) {
        console.error("Error fetching line items:", error);
      }
    };

    if (!isEditMode) {
      fetchLineItems();
    }
  }, [selectedOrder, isEditMode]);

  // Remove the useEffect that was causing continuous toggling

  useEffect(() => {
    if (isEditMode && editData) {
      setEmailSubject(editData.subject || "");
      setAttachmentType(editData.filetype?.toUpperCase() === "XLS" ? "XLS" : "CSV");
      setIncludeAiSummary(editData.include_ai_summary === true || editData.include_ai_summary === 1 || editData.include_ai_summary === "1");

      // Set emails from comma-separated string
      setEmails(editData.emailIds?.split(",").filter(Boolean) || []);

      // In edit mode, use the line items from editData or selectedItems prop
      // Check both possible property names for backward compatibility
      const lineItemIds = editData.hidden_lineitem_id || editData.line_item_ids || selectedItems;
      if (lineItemIds && lineItemIds.length > 0) {
        // Handle both string (comma-separated) and array formats
        const editLineItems = typeof lineItemIds === 'string'
          ? lineItemIds.split(',').filter(Boolean)
          : lineItemIds;
        setSelectedLineItems(editLineItems);
      } else {
        // If no line items found, try to use selectedItems prop as fallback
        if (selectedItems && selectedItems.length > 0) {
          setSelectedLineItems(selectedItems);
        }
      }
      let scheduleConfig: ScheduleConfig = {};
      try {
        scheduleConfig = JSON.parse(editData.future_send_Instuction || "{}");
      } catch (e) {
        console.error("Error parsing future_send_instruction:", e);
      }

      // Set schedule type based on editData.send_type
      setScheduleType(editData.send_type || "Daily");

      // Handle time for all schedule types
      const parseTimeFromInstruction = (timeStr: string | undefined) => {
        if (!timeStr) return null;
        // Convert "08am" to "08:00 AM" format
        const time = timeStr.toLowerCase();
        const period = time.includes('am') ? 'AM' : 'PM';
        const hourMin = time.replace(/[^0-9]/g, '');
        const hour = hourMin.slice(0, 2);
        const minute = hourMin.length > 2 ? hourMin.slice(2, 4) : '00';
        return `${hour}:${minute} ${period}`;
      };

      // Set time based on schedule type
      if (scheduleConfig.time) {
        setSelectedTime(parseTimeFromInstruction(scheduleConfig.time));
      }

      // Handle specific schedule type configurations
      if (editData.send_type === "Weekly") {
        setRepeatEvery(scheduleConfig.week === "TwoWeek" ? "TwoWeek" :
          scheduleConfig.week === "ThreeWeek" ? "ThreeWeek" : "OneWeek");
        // Parse days as comma-separated string
        if (scheduleConfig.days) {
          const daysArray = scheduleConfig.days.split(',').map((day: string) => {
            const trimmed = day.trim();
            // Normalize to 3-letter format with proper casing (Mon, Tue, Wed, etc.)
            const normalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
            return normalized.substring(0, 3);
          });
          setSelectedDays(daysArray);
        } else {
          setSelectedDays([]);
        }
      }
      else if (editData.send_type === "Monthly") {
        // Parse month days as comma-separated string
        if (scheduleConfig.month) {
          const monthDays = scheduleConfig.month.split(',').map((day: string) => {
            const trimmed = day.trim();
            // Remove leading zeros to match button values (01 -> 1, 02 -> 2, etc.)
            // Keep "Last" as-is
            return trimmed === "Last" ? trimmed : parseInt(trimmed, 10).toString();
          });
          setSelectedDays(monthDays);
        } else {
          setSelectedDays([]);
        }
      }
      else if (editData.send_type !== '' &&
		editData.send_type.toLowerCase() === 'ondate') {
        // Parse the future_send_date or send_date for OnDate type
        const dateToUse = editData.future_send_date || editData.send_date;
        if (dateToUse) {
          try {
            let dateObj;
            // Handle different date formats
            if (dateToUse.includes('/')) {
              // Format: MM/DD/YYYY or DD/MM/YYYY
              const parts = dateToUse.split(/[\/\s]/)[0].split('/');
              if (parts.length === 3) {
                const [month, day, year] = parts;
                dateObj = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
              }
            } else if (dateToUse.includes('-')) {
              // Format: YYYY-MM-DD
              dateObj = new Date(dateToUse.split(' ')[0]);
            } else {
              // Try parsing as-is
              dateObj = new Date(dateToUse.split(' ')[0]);
            }

            if (dateObj && !isNaN(dateObj.getTime())) {
              setSelectedDate(dateObj);
            }
          } catch (e) {
            console.error('Error parsing date:', e);
          }
        }
        // Parse time for OnDate
        if (scheduleConfig.time) {
          setSelectedTime(parseTimeFromInstruction(scheduleConfig.time));
        }
      }

      setModalLarge(true);
    }
  }, [isEditMode, editData, selectedItems, selectedOrder]);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const timeSlots = [
    { time: "08:00 AM", value: "08:00 AM" },
    { time: "12:00 PM", value: "12:00 PM" },
    { time: "03:00 PM", value: "03:00 PM" },
    { time: "06:00 PM", value: "06:00 PM" },
  ];

  const weekOptions = [
    { label: "1 Week", value: "OneWeek" },
    { label: "2 Weeks", value: "TwoWeek" },
    { label: "3 Weeks", value: "ThreeWeek" },
  ];

  const resetState = () => {
    setEmails([]);
    setEmailInput("");
    setEmailSubject("");
    setAttachmentType("XLS");
    setSelectedDate(new Date());
    setSelectedTime(null);
    setScheduleType("Daily");
    setRepeatEvery("OneWeek");
    setSelectedDays([]); // Always clear selected days
    setIncludeAiSummary(false);

    // Reset line items to all items
    if (lineItems.length > 0) {
      const allItemIds = lineItems.map((item: any) => item.Id);
      setSelectedLineItems(allItemIds);
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === "Enter" || e.key === ",") {
    e.preventDefault();
	const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();

    if (!trimmed || !validateEmail(trimmed) || emails.some(email => email.toLowerCase() === trimmed)) {
      toast.warning("Invalid or duplicate email");
      return; // do not add invalid/duplicate
    }

    // Valid email: add to list and clear input
    setEmails([...emails, trimmed]);
    setEmailInput("");
  }
};

const handleEmailBlur = () => {
  const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
  if (!trimmed) return;

  // Only run blur logic for Mobile
  if (isMobile) {
    if (!validateEmail(trimmed) || emails.some(email => email.toLowerCase() === trimmed)) {
      toast.warning("Invalid or duplicate email");
      return; // do not add invalid/duplicate
    }

    // Valid email: add to list and clear input
    setEmails([...emails, trimmed]);
    setEmailInput("");
  }
};

  const removeEmail = (index: number) => {
    const updated = [...emails];
    updated.splice(index, 1);
    setEmails(updated);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  function togLarge() {
    if (!selectedOrder) {
      toast.error("Please select an order first!");
      return;
    }
    if (!isEditMode && (selectedLineItems.length === 0 && selectedItems.length === 0)) {
      toast.error("No line items available for the selected order!");
      return;
    }
    // Reset other fields when opening modal
    if (!modalLarge) {
      resetState();
    }
    setModalLarge(!modalLarge);
  }

  const handleClose = () => {
    if (isEditMode && onClose) {
      onClose();
    } else {
      togLarge();
    }
  }

  const formatDate = (dateStr: any) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const [year, month, day] = dateStr.split("-");
    return `${day}/${months[parseInt(month) - 1]}/${year}`;
  };

  const createReport = async () => {
    var date = '';
    var range_labels = ['Today', 'Yesterday', 'Last 7 Days', 'Last 15 Days', 'This Week', 'Last Week', 'This Month', 'Last Month', 'Last 30 Days', 'This Year', 'Last Year', 'All Data'];
    var id = null;
    if (editData) {
      id = editData.id;
    }
    if (dateRangeLabel && range_labels.includes(dateRangeLabel)) {
      date = dateRangeLabel;
    } else {
      date = `${formatDate(currentStartDate)} - ${formatDate(currentEndDate)}`;
    }

    // Determine which line items to use
    const lineItemsToUse = isEditMode ? selectedLineItems : (selectedLineItems.length > 0 ? selectedLineItems : selectedItems);

    const data = {
      send: "send",
      all_data: "",
      hidden_order_name: selectedOrder,
      hidden_order_id: selectedOrder,
      hidden_date: date,
      hidden_to_emails: emails,
      hidden_send_type: scheduleType,
      hidden_subject: emailSubject,
      hidden_future_send_date: format(selectedDate, 'yyyy-MM-dd'),
      hidden_future_send_time: selectedTime ? selectedTime.toLowerCase().replace(/\s/g, "") : null,
      hidden_file_type: attachmentType.toLowerCase(),
      hidden_lineitem_id: lineItemsToUse,
      hidden_account_id: selectedAgency,
      hidden_manyWeeks: repeatEvery,
      // Only send relevant fields based on schedule type
      hidden_WeeklyDays: scheduleType === 'Weekly' ? selectedDays.join(',') : '',
      hidden_month: scheduleType === 'Monthly' ? selectedDays.join(',') : '',
      hidden_include_ai_summary: includeAiSummary,
      hidden_report_data_by: reportDataBy || 'week',
      id: id,
    };

    let hasError = false;

    if (emails.length === 0) {
      toast.error("Please add emails");
      hasError = true;
    }
    if (selectedTime === null) {
      toast.error("Please select report time");
      hasError = true;
    }
    if (emailSubject === "") {
      toast.error("Please enter email subject");
      hasError = true;
    }

    // Validate line items
    if (!lineItemsToUse || lineItemsToUse.length === 0) {
      toast.error("Please select at least 1 line item");
      hasError = true;
    }

    // Validate days count
    if ( selectedDays.length === 0 && (scheduleType === 'Weekly' || scheduleType === 'Monthly') ) {
      toast.error("Please select at least 1 day");
      hasError = true;
    }

    if (hasError) return;

    try {
      const response = await axiosInstance.post('/create-email-report', data);
      const status = response.data.status;
      const message = response.data.message;
      if (status === 200) {
        toast.success(message);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else if (status === 201) {
        toast.success(message);
        setTimeout(() => {
          handleClose();
          navigate("/email-reports", { state: { refresh: true } });
        }, 2000);
      } else {
        toast.error(message);
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        toast.error(data?.message || `Error: ${status}`);
      } else {
        toast.error('Network error or server not reachable');
      }
    }
  }

  const toggleDay = (day: string) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleMonthlyDay = (day: string) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  // Line item selection handlers
  const handleLineItemCheckboxChange = (itemId: any) => {
    setSelectedLineItems((prevSelected) => {
      // Normalize types to ensure consistent comparison
      const normalizedItemId = String(itemId);
      const normalizedSelected = prevSelected.map(String);

      const updatedSelectedItems = normalizedSelected.includes(normalizedItemId)
        ? prevSelected.filter((id) => String(id) !== normalizedItemId)
        : [...prevSelected, itemId];

      // Check if at least one item is selected
      if (updatedSelectedItems.length === 0) {
        toast.error("Please select at least 1 line item");
        return prevSelected; // Don't allow deselecting all items
      }

      return updatedSelectedItems;
    });
  };

  const handleLineItemSelectAllChange = (allItemIds: any[]) => {
    // Don't allow selecting nothing
    if (allItemIds.length === 0) {
      toast.error("Please select at least 1 line item");
      return;
    }
    setSelectedLineItems(allItemIds);
  };

  return (
    <>
      {!isEditMode && (
        <>
          <i
            className="fas fa-bell"
            style={{ fontSize: "xx-large", color: "#0a8dd4", cursor: "pointer",marginLeft:"12px" }}
            onClick={togLarge}
            title="Email Report"
          ></i> &nbsp;
        </>
      )}

      <Modal
       className="email-report-modal"
      contentLabel="Email Report Modal"
        isOpen={isEditMode ? showModal : modalLarge}
        toggle={handleClose}
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0">
            {!isEditMode ? "Email Report" : "Email Report: " + editData.subject}
          </h5>
          <button
            onClick={handleClose}
            type="button"
            className="close"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="px-3">
		  <div className="scrollContainer">
          <div className="mb-3">
            <label className="form-label">Email Report to:</label>
            <div className="d-flex align-items-center gap-2">
              <div className="flex-grow-1">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type Email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={handleEmailBlur}
                />
                <br></br>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>
            </div>
            {emails.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {emails.map((email, index) => (
                  <button key={index} className="badge btn btn-primary button-primary d-flex align-items-center">
                    {email}
                    <a
                      type="button"
                      className="btn-close btn-close-primary ms-2"
                      onClick={() => removeEmail(index)}
                      aria-label="Remove"
                      style={{ fontSize: '0.5rem' }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Line Item Selection Section - Only show in add mode */}
          {!isEditMode && (
            <div className="mb-3">
              <label className="form-label mb-2">Select Line Items:</label>
              <div className="d-flex align-items-center gap-2">
                <LineItemDropdown
                  lineItems={lineItems}
                  selectedItems={selectedLineItems}
                  handleCheckboxChange={handleLineItemCheckboxChange}
                  handleSelectAllChange={handleLineItemSelectAllChange}
                  module="email-report"
                />
                <small className="text-muted">
                  {selectedLineItems.length > 0
                    ? `${selectedLineItems.length} of ${lineItems.length} line items selected`
                    : `Please select line items`}
                </small>
                <div className="form-check ms-5 mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="includeAiSummary"
                    checked={includeAiSummary}
                    onChange={(e) => setIncludeAiSummary(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="includeAiSummary">
                    Include AI Summary
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Schedule Section */}
          <div className="mb-3">
            <label className="form-label">Set Delivery Schedule:</label>
            <div  className="mb-3"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "8px",
                  }}>
              {["Daily", "Weekly", "Monthly", "OnDate"].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    // Only clear selected days when actually changing schedule type
                    if (scheduleType !== type) {
                      setScheduleType(type);
                      setSelectedDays([]);
                    }
                  }}
                  id={type}
                  className={`${scheduleType === type ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                >
                  {type}
                </button>
              ))}
            </div>

            {scheduleType === "OnDate" ? (
              <div className="d-flex flex-column flex-lg-row gap-1 gap-md-3">
                <div className="email-report-calendar">
                  <Calendar
                    date={selectedDate}
                    onChange={(date: Date) => setSelectedDate(date)}
                    minDate={tomorrow}
                    locale={enUS}
                  />
                </div>
                <div className="d-flex flex-column">
                  <label className="form-label">Time Slot:</label>
                  <div className="d-flex flex-wrap gap-2">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.value}
                        className={`${selectedTime === slot.value ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                        onClick={() => setSelectedTime(slot.value)}
                        style={{padding:'6px',whiteSpace:'nowrap'}}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : scheduleType === "Weekly" ? (
              <div className="d-flex flex-column gap-4">
                <div>
                  <label className="form-label">Repeat Every:</label>

                  <UncontrolledDropdown className="widget-dropdown">
                    <DropdownToggle className="btn" tag="a">
                      {(repeatEvery == "OneWeek" ? "1 " : (repeatEvery == "TwoWeek" ? "2 " : "3 "))}
                      Week{repeatEvery != "OneWeek" ? "s" : ""}
                      <i className="mdi mdi-chevron-down ms-1"></i>
                    </DropdownToggle>
                    <DropdownMenu>
                      {weekOptions.map(({ label, value }) => (
                        <DropdownItem key={value} onClick={() => setRepeatEvery(value)}>
                          {label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>

                <div>
                  <label className="form-label">Select Days:</label>
                  <div className="d-flex gap-2 flex-wrap">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                      <button
                        key={day}
                        className={`${selectedDays.includes(day) ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                        onClick={() => toggleDay(day)}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="form-label">Time Slot:</label>
                  <div className="d-flex gap-2 flex-wrap">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.value}
                        className={`${selectedTime === slot.value ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                        onClick={() => setSelectedTime(slot.value)}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : scheduleType === "Monthly" ? (
              <div className="d-flex flex-column gap-4">
                {/* Day Selector */}
                <div>
                  <label className="form-label">Select Days:</label>
                  <div className="d-flex flex-wrap gap-2">
                    {[...Array(31).keys()].map((i) => {
                      const day = i + 1;
                      return (
                        <button
                          key={day}
                          type="button"
                          className={`rounded-pill ${selectedDays.includes(day.toString()) ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                          onClick={() => toggleMonthlyDay(day.toString())}
                          style={{ width: "40px", height: "40px", padding: 0, textAlign: "center" }}
                        >
                          {day}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className={`rounded-pill ${selectedDays.includes("Last") ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                      onClick={() => toggleMonthlyDay("Last")}
                    >
                      Last
                    </button>
                  </div>
                </div>

                {/* Time Slot Selector */}
                <div>
                  <label className="form-label">Time Slot:</label>
                  <div className="d-flex gap-2 flex-wrap">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.value}
                        className={`${selectedTime === slot.value ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                        onClick={() => setSelectedTime(slot.value)}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label className="form-label">Time Slot:</label>
                <div className="d-flex gap-2 flex-wrap">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.value}
                      className={`${selectedTime === slot.value ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                      onClick={() => setSelectedTime(slot.value)}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Attachment Type Section */}
          <div className="mb-3">
            <label className="form-label">Set Attachment Type:</label>
            <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)", 
                    gap: "8px", 
                  }}>
              <button
                className={`${attachmentType === 'XLS' ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                onClick={() => setAttachmentType("XLS")}
              >
                <img
                  id="export_excel"
                  className="cursor-pointer"
                  style={{ cursor: "pointer" }}
                  width={35}
                  height={25}
                  src={exportExcelIcon}
                  alt="Export to Excel"
                />
                XLS
              </button>
              <button
                className={`${attachmentType === 'CSV' ? 'btn btn-primary button-primary' : 'btn btn-light button-secondary'}`}
                onClick={() => setAttachmentType("CSV")}
              >
                <img
                  id="export_csv"
                  className="cursor-pointer"
                  style={{ cursor: "pointer" }}
                  width={35}
                  height={25}
                  src={exportCSVIcon}
                  alt="Export to CSV"
                />
                CSV
              </button>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2 pb-2">
          {isEditMode && (
            <button 
              onClick={handleClose}
              type="button"
              className="btn btn-danger"
              style={{
                  width: '88px'
              }}
            >
              Cancel
            </button>
          )}
          {!isEditMode && (
            <button
              className="btn btn-primary button-primary"
              // onClick="/email-reports"
              onClick={() => { navigate("/email-reports") }}
              title="View All Scheduled Reports"
            >
              View
            </button>
          )}
          <button className="btn btn-primary button-primary" onClick={createReport}>
            {!isEditMode ? "+ Create Report" : "Update Report"}
          </button>
        </div>
        </div>
      </Modal>
    </>
  );
}

export default EmailReport;
